import { Button } from '@HoudiniUI/Button';
import { Field, FieldContent, FieldDescription, FieldLabel, FieldLegend, FieldSet, FieldTitle } from '@HoudiniUI/Field';
import { RadioGroup, RadioGroupItem } from '@HoudiniUI/RadioGroup';
import { Select, SelectContent, SelectItem, SelectTrigger } from '@HoudiniUI/Select';
import {
	buildRegEdSubmissionMetadata,
	getRegEdComplianceErrorCode,
	IRegEdSubmissionInput,
	IRegEdSubmissionMetadata,
	isRegEdAdvisorSelectableFormat,
	REGED_DEFERRED_SUBMISSION,
	REGED_LEAD_TIME_OVERRIDE_REASON_MAX_LENGTH,
	RegEdComplianceErrorCode,
	useRegEdAnswersLeftToAccountUser,
	useRegEdIntegrationQuery,
	useRegEdMetadataSchemaQuery,
	useRegEdSubmissionValidateMutation,
	useRegEdSupportedFormatsQuery,
} from '@Queries/RegEdCompliance';
import * as Api from '@ViewModels';
import { css, StyleDeclarationValue } from 'aphrodite';
import moment from 'moment';
import * as React from 'react';
import { toast } from 'sonner';
import { baseStyleSheet } from '../../../../styles/styles';
import { LoadingSpinner } from '../../../LoadingSpinner';
import { ComplianceIcon } from '../../../svgs/icons/ComplianceIcon';
import { ChevronDown } from '../../../svgs/icons/ChevronDown';
import { RegEdMetadataForm } from '../../../compliance/RegEdMetadataForm';
import { CampaignType } from '../../../../../models/AdminModels';
import { useUserSession } from '../../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../../models/Logging';
import { getNonSendingDaysOfWeek } from '../../../../../models/UiUtils';
import { getCampaignNames } from '../campaignNaming';
import {
	applyEntryPointAnswers,
	applyReviewDateFloorAnswers,
	applyRevisionDateAnswers,
	findDateBeforeReviewComplete,
	findLeadTimeShortfall,
	findReviewCompleteAnswer,
	findSendFloorAnswer,
	findMissingRequiredFieldIds,
	revisionLockedFieldIds,
	stripHiddenFieldAnswers,
} from '../../../compliance/RegEdMetadataForm/regEdMetadataFormAdapter';
import { TextArea } from '../../../../components-v2/TextArea';
import { styleSheet as promptStyleSheet } from '../styles';
import { styleSheet } from './styles';

interface IProps {
	impersonationContext?: Api.IImpersonationContext;
	/** Answers the advertising-material question from where the advisor started. */
	campaignType?: CampaignType;
	/** The platforms this post actually targets, which answers the platform question. */
	socialProviders?: readonly Api.SocialMediaType[];
	/** For hosts that share their column with other content — collapses the questionnaire behind a button
	 * and lets the advisor size it. Off where the prompt already owns its panel. */
	collapsible?: boolean;
	disabled?: boolean;
	isLoading?: boolean;
	/** Seed the format/answers when re-submitting a revision, so the advisor edits the prior filing. */
	initialFormat?: string;
	initialAnswers?: Record<string, unknown>;
	/** A revision resubmits rather than schedules, so the hosts that drive one relabel the action. */
	submitLabel?: string;
	/**
	 * A revision derives its date questions rather than replaying them: review from the firm's current
	 * notice period, first use from `revisionSendDate`. Null where there is no send to follow — a blog may
	 * be backdated, so its first use stays with the advisor.
	 */
	isRevision?: boolean;
	revisionSendDate?: Date | null;
	/** The round being revised; the server holds a revision to that round's broker-dealer and notice rules. */
	revisesApprovalRequestId?: string | null;
	/** The user it is sent as, where already chosen; otherwise the server checks the caller's RegEd mapping. */
	ownerUserId?: string | null;
	/** Fires on every edit so the caller can hold a draft that survives back-navigation (re-seed via the initial props). */
	onFormStateChange?(metadata: IRegEdSubmissionMetadata): void;
	onScheduleRegEdCompliance(e: React.MouseEvent<HTMLElement>, metadata: IRegEdSubmissionInput): void;
	expanded?: boolean;
	onExpandedChange?(next: boolean): void;
	onScheduleWithoutCompliance?(e: React.MouseEvent<HTMLElement>): void;
	styles?: StyleDeclarationValue[];
}

const MIN_FORM_HEIGHT_PX = 220;
/**
 * Matches the host's own default so the panel opens where a drag can hold it. The reserve this replaced
 * existed to keep the submit action inside the column while the panel sat in flow; the host is now
 * anchored to the column's bottom edge, which keeps it there by construction.
 */
const MAX_FORM_HEIGHT_VIEWPORT_FRACTION = 0.9;

/** Hosts opt in by tagging their container `data-reged-host` and sizing it from this. */
export const REGED_HOST_HEIGHT_VAR = '--reged-host-height';

/**
 * The days the date of first use cannot land on. First use is the send, so it has to be a day the
 * channel can actually send on — but only email and newsletters schedule through the account's send
 * intervals. The social scheduler offers every day and every time and never reads them, so borrowing
 * them there would block a Saturday the advisor can genuinely post on.
 */
export const firstUseUnavailableDaysOfWeek = (
	campaignType: CampaignType | undefined,
	sendEmail: Partial<Api.ISendEmailFeatures> | undefined
): number[] | undefined =>
	campaignType === CampaignType.Email || campaignType === CampaignType.htmlNewsletter
		? getNonSendingDaysOfWeek(sendEmail)
		: undefined;

/** The scrolling questionnaire: labelled by the collapse toggle, and measured against by a drag. */
const QUESTIONNAIRE_ID = 'reged-questionnaire';

/**
 * Keeps a drag from shrinking the questionnaire to nothing or growing the panel past its host column.
 *
 * `furniturePx` is everything in the panel that cannot give way — the grip, the collapse toggle, the
 * prose, the submit button, and whatever the host stacks below the questionnaire. Only the
 * questionnaire scrolls, so a floor that ignores the rest lets a drag ask for less height than the
 * fixed content occupies, and that overflow is drawn over the host's own controls.
 *
 * The ceiling outranks the floor: the panel is an overlay anchored to the column's bottom edge, so a
 * height its host cannot hold spills out of the column rather than clipping.
 */
export const clampFormHeight = (desiredPx: number, viewportHeight = window.innerHeight, furniturePx = 0): number =>
	Math.min(viewportHeight * MAX_FORM_HEIGHT_VIEWPORT_FRACTION, Math.max(furniturePx + MIN_FORM_HEIGHT_PX, desiredPx));

/**
 * Hands the metadata payload back to the caller rather than submitting — the submissions POST can
 * only happen once the parent (and its server-built approval request) exists.
 */
const RegEdComplianceQuestionnaire: React.FC<IProps> = ({
	impersonationContext,
	campaignType,
	socialProviders,
	collapsible,
	disabled,
	isLoading,
	initialFormat,
	initialAnswers,
	submitLabel = 'Schedule for Compliance Review',
	isRevision,
	revisionSendDate,
	revisesApprovalRequestId,
	ownerUserId,
	onFormStateChange,
	onScheduleRegEdCompliance,
	onScheduleWithoutCompliance,
	expanded: expandedProp,
	onExpandedChange,
	styles = [],
}) => {
	const { ctaName, displayName } = getCampaignNames(campaignType);
	const [skipping, setSkipping] = React.useState(false);
	const choiceIdPrefix = React.useId();
	// The caller derives the bypass from observable state, so it can vanish mid-edit.
	const skipChosen = skipping && !!onScheduleWithoutCompliance;
	const [format, setFormat] = React.useState(initialFormat ?? '');
	const [shortNoticeReason, setShortNoticeReason] = React.useState('');
	// The server can find a shortfall the schema's minimum date didn't show — a stale schema, or no
	// minimum at all — and only a reason gets the advisor past it.
	const [serverShortNotice, setServerShortNotice] = React.useState(false);
	const [answers, setAnswers] = React.useState<Record<string, unknown>>(initialAnswers ?? {});
	// State set earlier in the same commit is not visible to a later effect, and the seed and the
	// preselect can land together once the schema is cached — so applied answers ride a ref too.
	const answersRef = React.useRef(answers);
	const applyAnswers = (next: Record<string, unknown>) => {
		answersRef.current = next;
		setAnswers(next);
	};
	const [missingFieldIds, setMissingFieldIds] = React.useState<ReadonlySet<string>>(new Set());
	// Separate from the missing-required set: an answered-but-too-early date is not "missing", so the
	// reconciliation that set runs through on every edit would clear the mark while submit stayed blocked.
	const [outOfOrderDateFieldIds, setOutOfOrderDateFieldIds] = React.useState<ReadonlySet<string>>(new Set());
	const [uncontrolledExpanded, setUncontrolledExpanded] = React.useState(!collapsible);
	const expanded = expandedProp ?? uncontrolledExpanded;
	const setExpanded = (next: boolean) => {
		if (onExpandedChange) {
			onExpandedChange(next);
			return;
		}
		setUncontrolledExpanded(next);
	};
	const containerRef = React.useRef<HTMLDivElement>(null);

	const userSession = useUserSession();
	const { logApiError } = useEventLogging('RegEdComplianceApprovalPrompt');
	// Logged whether or not it blocks: a validator that fails open would otherwise leave no trace.
	const validation = useRegEdSubmissionValidateMutation({
		onError: error => logApiError('RegEdSubmissionValidate-Error', Api.asApiError(error)),
	});
	// `isPending` only lands on a later render, so a quick second click would otherwise validate and hand off twice.
	const submittingRef = React.useRef(false);
	// The hand-off lands once validation settles, so it reads the host's current callback and state, not the click's.
	const latestHostRef = React.useRef({ disabled, onScheduleRegEdCompliance });
	// Written after commit, so a render React throws away can't leave its props behind for the hand-off.
	React.useEffect(() => {
		latestHostRef.current = { disabled, onScheduleRegEdCompliance };
	});
	const unavailableFirstUseDays = firstUseUnavailableDaysOfWeek(
		campaignType,
		impersonationContext?.account?.features?.sendEmail ?? userSession?.account?.features?.sendEmail
	);

	// The parent may resolve format and answers on different renders; latch each prop separately so
	// a late-arriving answers bag still seeds, and edits are never clobbered.
	const seededFormat = React.useRef(false);
	const seededAnswers = React.useRef(false);
	React.useEffect(() => {
		if (!seededFormat.current && initialFormat !== undefined) {
			setFormat(initialFormat);
			seededFormat.current = true;
		}
		if (!seededAnswers.current && initialAnswers !== undefined) {
			applyAnswers(initialAnswers);
			seededAnswers.current = true;
		}
	}, [initialFormat, initialAnswers]);

	const { data: supportedFormats, isLoading: formatsLoading } = useRegEdSupportedFormatsQuery({ impersonationContext });
	// Firm-level vendor content is filed by a Levitate admin, so the advisor never picks that format.
	const formats = (supportedFormats ?? []).filter(isRegEdAdvisorSelectableFormat);
	const onlyFormat = formats.length === 1 ? formats[0] : undefined;

	// One option isn't a choice. Picking it keeps the advisor from having to open a single-item menu
	// before the questionnaire will load — and makes "choose a format" unreachable rather than a step.
	// A seeded revision format is already set by then, so this never overrides one.
	React.useEffect(() => {
		if (onlyFormat && !format) {
			setFormat(onlyFormat);
			// Tell the caller too — a draft it holds would otherwise record no format at all until the
			// advisor happened to edit an answer. The `!format` guard keeps this to one firing.
			onFormStateChange?.(buildRegEdSubmissionMetadata(onlyFormat, answers));
		}
	}, [onlyFormat, format, answers, onFormStateChange]);

	const { data: schema, isLoading: schemaLoading } = useRegEdMetadataSchemaQuery(format || null, {
		impersonationContext,
	});

	// Collapsed, the panel is just its button — but a schema that never loaded needs to say so somewhere.
	const showDetail = !collapsible || expanded || !schema;
	// Hidden rather than unmounted so the format and answers survive a change of mind.
	const formHidden = !showDetail || skipChosen;

	// Preselecting fills blanks, so it has to happen once and then stop: an answer the advisor clears is blank
	// again, and a fill that re-ran would put it straight back and make the question impossible to change.
	// Keyed on what the preselection is derived from, so a late-arriving entry point still gets one pass.
	const entryPointKey = `${format}|${campaignType ?? ''}|${(socialProviders ?? []).join(',')}`;
	const appliedEntryPointKey = React.useRef<string | null>(null);
	React.useEffect(() => {
		if (!schema || appliedEntryPointKey.current === entryPointKey) {
			return;
		}
		// Latched even when there is nothing to fill, so an unmappable entry point doesn't retry every render.
		appliedEntryPointKey.current = entryPointKey;
		const entryPointAnswers = applyEntryPointAnswers(schema, answersRef.current, campaignType, socialProviders);
		if (!entryPointAnswers) {
			return;
		}
		applyAnswers(entryPointAnswers);
		onFormStateChange?.(buildRegEdSubmissionMetadata(format, entryPointAnswers));
	}, [schema, entryPointKey, campaignType, socialProviders, format, onFormStateChange]);

	// Ahead of the floor below, which then finds first use already on or after review. Waits for the
	// seeded answers: deriving from an empty bag and then being overwritten by the seed would file the stale dates.
	const revisionSendDay = isRevision && revisionSendDate ? moment(revisionSendDate).format('YYYY-MM-DD') : null;
	const appliedRevisionFor = React.useRef<string | null>(null);
	React.useEffect(() => {
		if (!isRevision || !schema || (initialAnswers !== undefined && !seededAnswers.current)) {
			return;
		}
		const revisionKey = `${format}|${revisionSendDay ?? ''}`;
		if (appliedRevisionFor.current === revisionKey) {
			return;
		}
		appliedRevisionFor.current = revisionKey;
		const derived = applyRevisionDateAnswers(schema, answersRef.current, revisionSendDay);
		if (!derived) {
			return;
		}
		applyAnswers(derived);
		onFormStateChange?.(buildRegEdSubmissionMetadata(format, derived));
	}, [isRevision, schema, initialAnswers, format, revisionSendDay, onFormStateChange]);

	// First use follows the review date: seeded when blank, raised when the review date moves past it.
	// Latched on the date rather than on the answers, so clearing it is not undone on the next
	// keystroke — and keyed with the format like the latch above, since switching format clears the
	// answers and a date-only latch would skip a new form answered the same day. The submit path
	// re-checks, which is what catches an edit made after this has already run.
	//
	// Key and payload both come off the ref: the state copy trails it within a commit where an earlier
	// effect has just written, so reading the key from state could describe a different answer set than
	// the one being floored.
	const appliedFloorFor = React.useRef<string | null>(null);
	React.useEffect(() => {
		if (!schema) {
			return;
		}
		const reviewDate = findReviewCompleteAnswer(schema, answersRef.current);
		const floorKey = reviewDate ? `${format}|${reviewDate}` : null;
		if (!floorKey || appliedFloorFor.current === floorKey) {
			return;
		}
		appliedFloorFor.current = floorKey;
		const floored = applyReviewDateFloorAnswers(schema, answersRef.current, unavailableFirstUseDays);
		if (!floored) {
			return;
		}
		applyAnswers(floored);
		onFormStateChange?.(buildRegEdSubmissionMetadata(format, floored));
	}, [schema, answers, format, onFormStateChange, unavailableFirstUseDays]);

	const handleFormatChange = (next: string) => {
		setFormat(next);
		applyAnswers({});
		// Field ids can repeat across schemas, so marks earned against the old form would otherwise
		// show the new one as invalid before it has been touched.
		setMissingFieldIds(new Set());
		setServerShortNotice(false);
		onFormStateChange?.(buildRegEdSubmissionMetadata(next, {}));
	};

	const handleAnswersChange = (next: Record<string, unknown>) => {
		// Cleared on any edit: the date the server refused need not be one the schema dates, and the next check
		// raises it again if the shortfall is still there.
		setServerShortNotice(false);
		applyAnswers(next);
		// Clear as they're filled in, so the form stops flagging what the advisor has already fixed.
		setMissingFieldIds(previous =>
			previous.size ? new Set(findMissingRequiredFieldIds(schema, next).filter(id => previous.has(id))) : previous
		);
		onFormStateChange?.(buildRegEdSubmissionMetadata(format, next));
	};

	// The height that matters belongs to the host's container, which sits above this component, so it rides a
	// custom property rather than a prop — the host keeps its own default until the advisor drags.
	const startResize = (e: React.PointerEvent<HTMLDivElement>) => {
		const startY = e.clientY;
		const startHeight = containerRef.current?.closest('[data-reged-host]')?.getBoundingClientRect().height ?? 0;
		// Measured per drag rather than declared: it depends on which host is rendering the panel and on
		// how the prose above the questionnaire wraps at that host's width.
		const formHeight = containerRef.current?.querySelector(`#${QUESTIONNAIRE_ID}`)?.getBoundingClientRect().height ?? 0;
		const furnitureHeight = Math.max(0, startHeight - formHeight);
		e.currentTarget.setPointerCapture(e.pointerId);
		// The grip is the panel's top edge and the panel is anchored below it, so dragging down shrinks it.
		const onMove = (move: PointerEvent) =>
			document.documentElement.style.setProperty(
				REGED_HOST_HEIGHT_VAR,
				`${clampFormHeight(startHeight - (move.clientY - startY), window.innerHeight, furnitureHeight)}px`
			);
		const onUp = () => {
			window.removeEventListener('pointermove', onMove);
			window.removeEventListener('pointerup', onUp);
		};
		window.addEventListener('pointermove', onMove);
		window.addEventListener('pointerup', onUp);
	};

	// Server-computed, so the window is never duplicated here — a shortfall is simply an answer
	// earlier than the minimum the schema came back with.
	const shortfall = findLeadTimeShortfall(schema, answers);
	const needsShortNoticeReason = !!shortfall || serverShortNotice;
	const sendNoun = campaignType === CampaignType.Social ? 'posting date' : 'send time';
	const lockedDates = isRevision ? revisionLockedFieldIds(schema, answers, !!revisionSendDay) : {};
	const lockedFieldNotes = new Map<string, string>();
	if (lockedDates.reviewFieldId) {
		lockedFieldNotes.set(
			lockedDates.reviewFieldId,
			revisionSendDay
				? `Set from your firm's notice period and the ${sendNoun}.`
				: "Set from your firm's notice period."
		);
	}
	if (lockedDates.firstUseFieldId) {
		lockedFieldNotes.set(lockedDates.firstUseFieldId, `Matches the ${sendNoun}.`);
	}
	// A locked review date moves only with the send, so that is what a short-notice revision has to change.
	const laterChoice = lockedDates.reviewFieldId && revisionSendDay ? `a later ${sendNoun}` : 'a later date';
	const trimmedReason = shortNoticeReason.trim();
	const reasonTooLong = trimmedReason.length > REGED_LEAD_TIME_OVERRIDE_REASON_MAX_LENGTH;

	const handleSubmit = (e: React.MouseEvent<HTMLElement>) => {
		// Hosts cancel the click from their callback, which runs once validation settles — too late to take effect.
		e.preventDefault();
		if (submittingRef.current) {
			return;
		}
		if (!format) {
			toast.error('Please choose a submission format.');
			return;
		}
		const missing = findMissingRequiredFieldIds(schema, answers);
		setMissingFieldIds(new Set(missing));
		if (missing.length > 0) {
			toast.error(`Please complete all required fields (${missing.length} remaining).`);
			// The questionnaire is long enough that a count alone leaves the advisor hunting. Targets the
			// field id rather than [data-invalid], which this render hasn't applied yet.
			containerRef.current
				?.querySelector(`[data-field-id="${CSS.escape(missing[0])}"]`)
				// Optional call: jsdom has no scrollIntoView, and this is a nicety either way.
				?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
			return;
		}
		if (needsShortNoticeReason && !trimmedReason) {
			toast.error(`Choose ${laterChoice}, or say why you need the review sooner.`);
			return;
		}
		if (reasonTooLong) {
			toast.error(`Please keep the reason to ${REGED_LEAD_TIME_OVERRIDE_REASON_MAX_LENGTH} characters or fewer.`);
			return;
		}
		// Resolved from the answers actually being filed, not the raw ones — a question hidden by a
		// visibility rule keeps its answer in state but never reaches RegEd.
		const filed = stripHiddenFieldAnswers(schema, answers);
		// The pickers bound this, but a typed answer reaches onChange unchecked, and nothing downstream
		// bounds it either — the send floor is clamped against today, never against the review date.
		const tooEarly = findDateBeforeReviewComplete(schema, filed);
		setOutOfOrderDateFieldIds(new Set());
		if (tooEarly) {
			toast.error(
				`${tooEarly.field.label ?? 'That date'} cannot be before the review is complete on ${tooEarly.reviewCompleteDate}.`
			);
			setOutOfOrderDateFieldIds(new Set(tooEarly.field.id ? [tooEarly.field.id] : []));
			return;
		}
		// The send is the material going into use, so it is bounded by the date of first use rather than
		// by the review deadline that only has to precede it.
		const sendFloor = findSendFloorAnswer(schema, filed);
		// Clamped here because the schedulers coalesce a null floor to today but use a non-null one
		// verbatim, so a date already past would lower their floor instead of failing to bind.
		const earliestSendDate =
			sendFloor && !moment(sendFloor, 'YYYY-MM-DD').isBefore(moment(), 'day') ? sendFloor : undefined;
		const metadata = buildRegEdSubmissionMetadata(format, filed);
		const reviewLeadTimeOverrideReason = needsShortNoticeReason ? trimmedReason : undefined;
		const handOff = (submission: IRegEdSubmissionInput) => {
			// Still mounted, so the advisor is looking at a form that went quiet; a closed form never reaches here.
			if (latestHostRef.current.disabled) {
				toast.error('Something changed while we checked your answers. Review the form and submit again.');
				return;
			}
			latestHostRef.current.onScheduleRegEdCompliance(e, submission);
		};
		const handOffAnswers = () =>
			handOff({
				...metadata,
				...(reviewLeadTimeOverrideReason ? { reviewLeadTimeOverrideReason } : {}),
				...(earliestSendDate ? { earliestSendDate } : {}),
			});
		submittingRef.current = true;
		// Checked before the host schedules anything: the filing itself only happens once the parent exists,
		// and a rejection then arrives after the advisor has lost the form. Per-call callbacks are dropped
		// once the prompt unmounts, so a host that closed the form mid-check files nothing and hears nothing.
		validation.mutate(
			{
				request: {
					metadata,
					revisesApprovalRequestId: revisesApprovalRequestId ?? null,
					...(reviewLeadTimeOverrideReason ? { reviewLeadTimeOverrideReason } : {}),
					...(ownerUserId ? { ownerUserId } : {}),
				},
				// The route requires a user when impersonating; an account-only context 404s and skips the check.
				impersonationContext: Api.ImpersonationBroker.withForUser(impersonationContext, ownerUserId ?? undefined),
			},
			{
				onSuccess: handOffAnswers,
				onError: error => {
					const apiError = Api.asApiError(error);
					// Only a coded rejection is the advisor's to fix. Anything else fails open, because the create
					// re-runs these same checks and an unreachable validator must not block scheduling.
					const errorCode = getRegEdComplianceErrorCode(apiError);
					if (errorCode === undefined) {
						handOffAnswers();
						return;
					}
					// An admin reaches the questions only when officer filing couldn't be read; the API's answer is
					// that they aren't theirs to file, so it schedules with them left to the account's user.
					if (errorCode === RegEdComplianceErrorCode.FilingRequiresAccountUser) {
						handOff(REGED_DEFERRED_SUBMISSION);
						return;
					}
					if (errorCode === RegEdComplianceErrorCode.ReviewLeadTimeTooSoon) {
						setServerShortNotice(true);
					}
					toast.error(
						apiError?.systemMessage || 'Your compliance answers need attention before this can be scheduled.'
					);
				},
				onSettled: () => {
					submittingRef.current = false;
				},
			}
		);
	};

	return (
		<div
			className={css(styleSheet.container, collapsible && styleSheet.containerCollapsible, ...styles)}
			ref={containerRef}
		>
			{onScheduleWithoutCompliance ? (
				<FieldSet>
					<FieldLegend>{`Does this ${displayName} need compliance review?`}</FieldLegend>
					<FieldDescription>
						Submissions go to your firm's compliance system for review. Not everything you send requires it.
					</FieldDescription>
					<RadioGroup
						value={skipChosen ? 'skip' : 'review'}
						onValueChange={value => {
							const skip = value === 'skip';
							setSkipping(skip);
							setExpanded(!skip);
						}}
						disabled={disabled || isLoading}
						styles={[styleSheet.choiceGroup]}
					>
						<FieldLabel htmlFor={`${choiceIdPrefix}-review`}>
							<Field orientation='horizontal'>
								<FieldContent>
									<FieldTitle>Yes — submit for review</FieldTitle>
									<FieldDescription>
										Complete your firm's questionnaire. It's filed and held until a reviewer decides.
									</FieldDescription>
								</FieldContent>
								<RadioGroupItem value='review' id={`${choiceIdPrefix}-review`} />
							</Field>
						</FieldLabel>
						<FieldLabel htmlFor={`${choiceIdPrefix}-skip`}>
							<Field orientation='horizontal'>
								<FieldContent>
									<FieldTitle>No — send without compliance</FieldTitle>
									<FieldDescription>
										{`Nothing is filed. The ${displayName} sends on the schedule you set.`}
									</FieldDescription>
								</FieldContent>
								<RadioGroupItem value='skip' id={`${choiceIdPrefix}-skip`} />
							</Field>
						</FieldLabel>
					</RadioGroup>
				</FieldSet>
			) : null}

			{collapsible && schema && expanded && !skipChosen ? (
				<div className={css(styleSheet.resizeHandle)} data-testid='reged-resize-grip' onPointerDown={startResize}>
					<div className={css(styleSheet.resizeGrip)} />
				</div>
			) : null}

			{collapsible && schema && !skipChosen ? (
				<Button
					variant='primary'
					styles={[styleSheet.collapseToggle]}
					aria-expanded={expanded}
					aria-controls={QUESTIONNAIRE_ID}
					onClick={() => setExpanded(!expanded)}
				>
					<span>{expanded ? 'Hide compliance form' : 'Fill out compliance form'}</span>
					<ChevronDown
						width={16}
						height={16}
						className={css(styleSheet.collapseChevron, expanded && styleSheet.collapseChevronOpen)}
					/>
				</Button>
			) : null}

			{!onScheduleWithoutCompliance && showDetail ? (
				<>
					<div className={css(styleSheet.title)}>Submit for compliance review</div>
					<div className={css(styleSheet.detail)}>
						{onlyFormat
							? 'Complete the compliance questionnaire.'
							: 'Choose the submission format and complete the compliance questionnaire.'}{' '}
						This is sent to your firm's compliance system for review.
					</div>
				</>
			) : null}

			{/* The class does the hiding: this field's own `display: flex` beats the UA rule for `hidden`. */}
			<div className={css(styleSheet.field, formHidden && styleSheet.hiddenField)} hidden={formHidden}>
				<span className={css(styleSheet.label)}>Format</span>
				{onlyFormat ? (
					// Shown rather than hidden: this is a regulatory filing, so what it is filed under is
					// worth seeing even when there is nothing to decide. Reads the chosen format, not the
					// sole offered one — a revision keeps the format it was originally filed under, which
					// the firm may no longer offer.
					<span className={css(styleSheet.formatValue)}>{format || onlyFormat}</span>
				) : (
					<Select
						value={format}
						onValueChange={handleFormatChange}
						disabled={disabled || isLoading || formatsLoading || formats.length === 0}
					>
						<SelectTrigger styles={styleSheet.selectTrigger}>
							<span>{format || (formatsLoading ? 'Loading formats…' : 'Select a format…')}</span>
						</SelectTrigger>
						<SelectContent>
							{formats.map(f => (
								<SelectItem key={f} value={f}>
									{f}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				)}
				{!formatsLoading && formats.length === 0 ? (
					<span className={css(styleSheet.detail)}>No submission formats are configured for your firm.</span>
				) : null}
			</div>

			{format && schemaLoading && !skipChosen ? (
				<div className={css(styleSheet.detail)}>Loading questionnaire…</div>
			) : null}
			{format && !schemaLoading && !schema && !skipChosen ? (
				<div className={css(styleSheet.detail)}>No compliance questionnaire is configured for this format.</div>
			) : null}
			{schema && expanded && !skipChosen ? (
				<div id={QUESTIONNAIRE_ID} className={css(collapsible && styleSheet.scrollingForm)}>
					<RegEdMetadataForm
						schema={schema}
						answers={answers}
						onChange={handleAnswersChange}
						disabled={disabled || isLoading}
						invalidFieldIds={new Set([...missingFieldIds, ...outOfOrderDateFieldIds])}
						firstUseUnavailableDaysOfWeek={unavailableFirstUseDays}
						lockedFieldNotes={lockedFieldNotes.size ? lockedFieldNotes : undefined}
					/>
					{/* Inside the scroll region, with the date question it is about: the panel's height is
					    capped by its host, and only this region gives way, so prose and a textarea pinned
					    below it push the submit action out of the column instead. */}
					{needsShortNoticeReason ? (
						<div className={css(styleSheet.field, styleSheet.shortNotice)}>
							<span className={css(styleSheet.detail)}>
								{shortfall ? (
									<>
										{/* Non-breaking: the date is the point of the sentence, so it must not wrap away from it. */}
										Your firm asks for more notice than that. The earliest date you can request is{'\u00a0'}
										<strong>{shortfall.minimumDate}</strong>. Pick {laterChoice}, or tell your reviewer why you need it
										sooner.
									</>
								) : (
									<>
										Your firm asks for more notice than that. Pick {laterChoice}, or tell your reviewer why you need it
										sooner.
									</>
								)}
							</span>
							<TextArea
								value={shortNoticeReason}
								onChange={event => setShortNoticeReason(event.target.value)}
								placeholder='Your compliance reviewer will read this.'
								disabled={disabled || isLoading}
								rows={3}
							/>
							{reasonTooLong ? (
								<span className={css(styleSheet.detail)}>
									Please keep this to {REGED_LEAD_TIME_OVERRIDE_REASON_MAX_LENGTH} characters or fewer.
								</span>
							) : null}
						</div>
					) : null}
				</div>
			) : null}

			{!collapsible || expanded || skipChosen ? (
				skipChosen && onScheduleWithoutCompliance ? (
					<Button
						variant='secondary'
						disabled={disabled || isLoading}
						isLoading={isLoading}
						onClick={onScheduleWithoutCompliance}
					>
						{`Send ${ctaName} without Compliance`}
					</Button>
				) : (
					<Button
						disabled={
							disabled ||
							isLoading ||
							validation.isPending ||
							!schema ||
							(needsShortNoticeReason && (!trimmedReason || reasonTooLong))
						}
						isLoading={isLoading || validation.isPending}
						onClick={handleSubmit}
					>
						{submitLabel}
					</Button>
				)
			) : null}
		</div>
	);
};

/**
 * Submissions are filed under the advisor's AdTrax identity, so an admin from outside the account
 * schedules without answering and leaves the questions to the account's user. Every host renders the
 * questionnaire through this — scheduling, revising and finishing — so the rule holds in one place.
 *
 * Officer-filing accounts keep the questionnaire here: their admins are routed to the officer flow
 * before they reach it when scheduling, and the deferral endpoint refuses them.
 */
export const RegEdComplianceApprovalPrompt: React.FC<IProps> = props => {
	const {
		campaignType,
		collapsible,
		disabled,
		impersonationContext,
		isLoading,
		onScheduleRegEdCompliance,
		styles = [],
		submitLabel,
	} = props;
	const { isLoading: officerFilingLoading, leftToAccountUser } = useRegEdAnswersLeftToAccountUser(impersonationContext);
	const { data: integration } = useRegEdIntegrationQuery({ enabled: leftToAccountUser, impersonationContext });
	const { ctaName, displayName } = getCampaignNames(campaignType);
	const leadTimeDays = integration?.reviewLeadTimeDays ?? 0;
	// The window is counted from when the answers are filed, which is later than now; flooring the send at
	// it is the soonest date that can avoid a short-notice reason, not a guarantee of it.
	const handover: IRegEdSubmissionInput =
		leadTimeDays > 0
			? {
					...REGED_DEFERRED_SUBMISSION,
					earliestSendDate: moment().add(leadTimeDays, 'days').format('YYYY-MM-DD'),
					reviewNotice: { brokerDealerName: integration?.brokerDealerName, days: leadTimeDays },
				}
			: REGED_DEFERRED_SUBMISSION;

	if (!leftToAccountUser) {
		return <RegEdComplianceQuestionnaire {...props} />;
	}
	if (officerFilingLoading) {
		return (
			<div className={css(promptStyleSheet.container, promptStyleSheet.regEdProbePlaceholder, ...styles)}>
				<LoadingSpinner type='large' />
			</div>
		);
	}
	return (
		<div className={`${css(promptStyleSheet.container, ...styles)} compliance-approval-prompt`}>
			{collapsible ? null : (
				<div className={css(promptStyleSheet.iconContainer)}>
					<ComplianceIcon className={css(promptStyleSheet.icon)} />
				</div>
			)}
			<div>The account's user answers the compliance questions</div>
			<div className={css(promptStyleSheet.detailMessage)}>
				{`This ${displayName} is filed under its owner's RegEd identity, so they answer its compliance questions themselves. It will be held until they do; they'll find it on their dashboard.`}
			</div>

			<div className={css(baseStyleSheet.verticalStack, promptStyleSheet.stack)}>
				<Button
					disabled={disabled || isLoading}
					isLoading={isLoading}
					onClick={(e: React.MouseEvent<HTMLElement>) => onScheduleRegEdCompliance(e, handover)}
				>
					{submitLabel ?? `Schedule ${ctaName} with Compliance Pending`}
				</Button>
			</div>
		</div>
	);
};
